import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@services/auth.service';

/**
 * 👤 **GUARD DE INVITADO (GUEST)**
 *
 * **Patrón Implementado**: Guard Pattern + Inverse Authentication Strategy
 *
 * **Funcionalidades:**
 * - ✅ Protege rutas que SOLO deben ser accesibles por usuarios NO autenticados
 * - ✅ Ejemplos: login, registro, recuperar contraseña
 * - ✅ Redirige usuarios ya autenticados al dashboard
 *
 * **Uso:**
 * ```typescript
 * {
 *   path: 'login',
 *   component: LoginComponent,
 *   canActivate: [guestGuard]
 * }
 * ```
 *
 * **Flujo de Validación:**
 * 1. Verificar si el usuario YA está autenticado
 * 2. Si NO está autenticado → Permitir acceso (mostrar login/registro)
 * 3. Si YA está autenticado → Redirigir a dashboard
 *
 * @param route - Información de la ruta activada
 * @param state - Estado del router
 * @returns true si puede acceder (no autenticado), false si debe redirigir
 */
export const guestGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // 🔍 Verificar si ya está autenticado
  const token = authService.getAccessToken();
  const userData = authService.getUserData();

  if (token && userData && authService.isAuthenticated()) {
    // ✅ Ya está autenticado - redirigir al dashboard
    router.navigate(['/dashboard']);
    return false;
  }

  // 🚪 No está autenticado - permitir acceso a páginas de invitado
  return true;
};
